import React, { useEffect, useMemo, useState } from "react";
import {
  listClientForms,
  getClientForm,
  deleteClientForm,
  listScenarios,
  buildScenarioPrompt,
  startCall,
} from "../../api";
import ClientWebCallV2Button from "../../components/ClientWebCallV2Button";
import { useNotifications } from "../../components/NotificationsBell";
import { getCallDebtEntries, getDebtsArray, isDebtPaid } from "../../utils/callDebts";
import {
  getClientHistoryEntries,
  getLatestClientHistoryEntry,
} from "../../utils/clientHistory";

const CARD = "rounded-2xl border border-[#1f2638] bg-[#0e1320]/50 p-5";
const LBL = "text-xs text-slate-400";
const H2 = "text-sm font-semibold tracking-wider text-slate-200";
const INP =
  "w-full rounded-xl border border-[#1f2638] bg-[#0f1523] px-3 py-2 text-sm text-slate-200 placeholder:text-slate-500";

const BTN_GHOST =
  "inline-flex items-center justify-center rounded-xl border border-transparent bg-transparent px-3 py-2 text-sm font-semibold hover:bg-[#0e1320] disabled:opacity-50 disabled:cursor-not-allowed";

const BTN =
  "inline-flex items-center justify-center gap-2 rounded-xl border border-[#1f2638] bg-[#121825] px-4 py-2 text-sm font-semibold hover:bg-[#141b2d] disabled:opacity-60 disabled:cursor-not-allowed";

const BTN2 = "inline-flex items-center justify-center gap-2 rounded-xl border border-[rgb(15_35_24)] bg-[rgb(21_47_31)] px-4 py-2 text-sm font-semibold text-green-100 hover:bg-[rgb(26_58_38)] focus:outline-none focus:ring-2 focus:ring-[rgb(34_85_55)] disabled:opacity-60 disabled:cursor-not-allowed";

const BTN_PRIMARY =
  "inline-flex items-center justify-center gap-2 rounded-xl border border-violet-600/40 bg-violet-600/20 px-4 py-2 text-sm font-semibold hover:bg-violet-600/30 disabled:opacity-60 disabled:cursor-not-allowed";

const BTN_DANGER =
  "inline-flex items-center justify-center gap-2 rounded-xl border border-rose-600/40 bg-rose-600/15 px-4 py-2 text-sm font-semibold hover:bg-rose-600/25 disabled:opacity-60 disabled:cursor-not-allowed";

const BTN_PAG =
  "inline-flex items-center justify-center rounded-xl border border-[#1f2638] bg-[#0f1523] px-3 py-2 text-sm font-semibold hover:bg-[#121825] disabled:opacity-50 disabled:cursor-not-allowed";

const PER_PAGE = 5;
const REGULAR_PAYER_SCENARIO_ID = "695f76a63de1300129b316c3";
const REGULAR_PAYER_SCENARIO_NAME = "Редовен платец";
const REGULAR_PAYER_SCENARIO_ID_NORM = REGULAR_PAYER_SCENARIO_ID.toLowerCase();
const REGULAR_PAYER_SCENARIO_NAME_NORM = REGULAR_PAYER_SCENARIO_NAME.toLowerCase();
const BROKEN_PROMISE_SCENARIO_NAME = "Неспазено обещание";
const BROKEN_PROMISE_SCENARIO_NAME_NORM = BROKEN_PROMISE_SCENARIO_NAME.toLowerCase();
const THIRD_PERSON_SCENARIO_NAME = "Трето лице";
const THIRD_PERSON_SCENARIO_NAME_NORM = THIRD_PERSON_SCENARIO_NAME.toLowerCase();

function dash(v) {
  return v === null || v === undefined || String(v).trim() === "" ? "—" : String(v);
}
function formatMoney(v) {
  if (v === null || v === undefined) return "";
  const s = String(v).trim();
  if (!s) return "";

  const normalized = s.replace(",", ".");
  if (!/^\d+(\.\d{1,2})?$/.test(normalized)) return s;

  const n = Number(normalized);
  if (!Number.isFinite(n)) return s;

  return new Intl.NumberFormat("bg-BG", {
    minimumFractionDigits: Number.isInteger(n) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(n);
}
function dashMoney(v) {
  const formatted = formatMoney(v);
  return formatted || "—";
}
function fmtDateISO(iso) {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return dash(iso);
  const [y, m, d] = iso.split("-");
  return `${d}.${m}.${y}`;
}
function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${dd}.${mm}.${yy} ${hh}:${mi}`;
}

const CALL_RESULT_CODE_TO_BG = {
  promise_to_pay: "Обещание за плащане",
  refuse_to_pay: "Отказ от плащане",
  no_answer: "Няма връзка",
  wrong_number: "Грешен номер",
  third_person: "Трето лице",
};

function mapCallResult(v) {
  if (!v) return "—";
  return CALL_RESULT_CODE_TO_BG[v] || dash(v);
}

function mapYesNo(v) {
  const m = { yes: "Да", no: "Не" };
  return m[v] || dash(v);
}

function normalizeScenarioToken(v) {
  return String(v || "").trim().toLowerCase();
}

function isRegularPayerScenarioSelected(scenarioValue, scenarios = []) {
  const currentNorm = normalizeScenarioToken(scenarioValue);
  if (!currentNorm) return false;

  if (currentNorm === REGULAR_PAYER_SCENARIO_ID_NORM || currentNorm === REGULAR_PAYER_SCENARIO_NAME_NORM) {
    return true;
  }

  const byId = (Array.isArray(scenarios) ? scenarios : []).find(
    (s) => normalizeScenarioToken(s?.id) === currentNorm
  );
  if (normalizeScenarioToken(byId?.name) === REGULAR_PAYER_SCENARIO_NAME_NORM) return true;

  const byName = (Array.isArray(scenarios) ? scenarios : []).find(
    (s) => normalizeScenarioToken(s?.name) === currentNorm
  );
  return normalizeScenarioToken(byName?.id) === REGULAR_PAYER_SCENARIO_ID_NORM;
}

function isBrokenPromiseScenarioSelected(scenarioValue, scenarios = []) {
  const currentNorm = normalizeScenarioToken(scenarioValue);
  if (!currentNorm) return false;

  if (currentNorm === BROKEN_PROMISE_SCENARIO_NAME_NORM) {
    return true;
  }

  const byId = (Array.isArray(scenarios) ? scenarios : []).find(
    (s) => normalizeScenarioToken(s?.id) === currentNorm
  );
  const byIdNameNorm = normalizeScenarioToken(byId?.name);
  if (byIdNameNorm === BROKEN_PROMISE_SCENARIO_NAME_NORM) {
    return true;
  }

  const byName = (Array.isArray(scenarios) ? scenarios : []).find(
    (s) => normalizeScenarioToken(s?.name) === currentNorm
  );
  const byNameNorm = normalizeScenarioToken(byName?.name);
  return byNameNorm === BROKEN_PROMISE_SCENARIO_NAME_NORM;
}

function isThirdPersonScenarioSelected(scenarioValue, scenarios = []) {
  const currentNorm = normalizeScenarioToken(scenarioValue);
  if (!currentNorm) return false;

  if (currentNorm === THIRD_PERSON_SCENARIO_NAME_NORM) return true;

  const byId = (Array.isArray(scenarios) ? scenarios : []).find(
    (s) => normalizeScenarioToken(s?.id) === currentNorm
  );
  return normalizeScenarioToken(byId?.name) === THIRD_PERSON_SCENARIO_NAME_NORM;
}

export default function FormView({ onBack, onAdd, onEdit }) {
  const {
    isClientCallActive,
    markClientCallPending,
    ensureClientCallState,
    refresh: refreshNotifications,
  } = useNotifications();
  const [q, setQ] = useState("");
  const [qDebounced, setQDebounced] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setQDebounced(q.trim()), 700);
    return () => clearTimeout(t);
  }, [q]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const [items, setItems] = useState([]);
  const [loadingList, setLoadingList] = useState(false);

  const [selectedId, setSelectedId] = useState("");
  const [selected, setSelected] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const [err, setErr] = useState("");

  const [reloadKey, setReloadKey] = useState(0);
  const [toast, setToast] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [outboundBusy, setOutboundBusy] = useState(false);

  const [scenarios, setScenarios] = useState([]);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        const arr = await listScenarios();
        if (!alive) return;
        setScenarios(arr || []);
      } catch {
        if (!alive) return;
        setScenarios([]);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2200);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    if (q.trim() !== qDebounced) return;

    let alive = true;

    const run = async () => {
      try {
        setErr("");
        setLoadingList(true);

        const data = await listClientForms({
          page,
          perPage: PER_PAGE,
          q: qDebounced,
        });

        if (!alive) return;

        const arr = Array.isArray(data) ? data : data?.items || [];
        const tp = Math.max(1, Number(data?.totalPages || data?.total_pages || 1));
        const t = Number(data?.total ?? arr.length);

        if (page > tp) {
          setPage(tp);
          return;
        }

        setItems(arr);
        setTotalPages(tp);
        setTotal(t);

        setSelectedId((prev) => {
          const keep = prev && arr.some((x) => x?.id === prev);
          return keep ? prev : arr[0]?.id || "";
        });
      } catch (e) {
        if (!alive) return;
        setErr(e?.message || "Грешка при зареждане");
        setItems([]);
        setSelectedId("");
        setTotalPages(1);
        setTotal(0);
      } finally {
        if (alive) setLoadingList(false);
      }
    };

    run();
    return () => {
      alive = false;
    };
  }, [q, qDebounced, page, reloadKey]);


  useEffect(() => {
    let alive = true;

    const run = async () => {
      if (!selectedId) {
        setSelected(null);
        return;
      }
      try {
        setErr("");
        setLoadingDetail(true);
        const data = await getClientForm(selectedId);
        if (!alive) return;
        setSelected(data);
      } catch (e) {
        if (!alive) return;
        setErr(e?.message || "Грешка при детайл");
        setSelected(null);
      } finally {
        if (alive) setLoadingDetail(false);
      }
    };

    run();
    return () => {
      alive = false;
    };
  }, [selectedId]);

  const filtered = useMemo(() => items, [items]);

  const payload = useMemo(() => {
    if (selected?.id) return selected;
    const hit = items.find((x) => x?.id === selectedId);
    return hit || null;
  }, [selected, items, selectedId]);

  const selectedMeta = payload;
  const payloadHistoryEntries = useMemo(
    () => getClientHistoryEntries(payload?.history),
    [payload?.history]
  );
  const latestPayloadHistory = useMemo(
    () => getLatestClientHistoryEntry(payload?.history),
    [payload?.history]
  );

  const isThirdPersonScenario = useMemo(
    () => isThirdPersonScenarioSelected(payload?.scenario, scenarios),
    [payload?.scenario, scenarios]
  );
  const defaultPhone = String(payload?.contacts?.phone || "").trim();
  const thirdPersonPhone = String(payload?.contacts?.thirdPersonPhone || "").trim();
  const outboundPhone = isThirdPersonScenario ? thirdPersonPhone : defaultPhone;
  const outboundClientId = String(payload?._id || payload?.id || "").trim();
  const canOutboundCall = !!(outboundPhone && outboundClientId);
  const outboundCallActive = isClientCallActive(outboundClientId);
  const outboundDisabled = !canOutboundCall || outboundBusy || deleting || loadingDetail || outboundCallActive;
  const outboundButtonTitle = !canOutboundCall
    ? (outboundClientId
      ? (isThirdPersonScenario ? "Липсва телефон на трето лице" : "Няма телефон")
      : "Липсва client id")
    : outboundCallActive
      ? "За този длъжник вече има активно обаждане"
      : "Обаждане по номер";

  const fromIdx = total === 0 ? 0 : (page - 1) * PER_PAGE + 1;
  const toIdx = Math.min(page * PER_PAGE, total);

  useEffect(() => {
    if (!outboundClientId) return;
    void ensureClientCallState(outboundClientId);
  }, [outboundClientId, ensureClientCallState]);

  const doDelete = async () => {
    const id = selectedMeta?.id || selectedId;
    if (!id) return;

    const ok = window.confirm("Сигурен ли си, че искаш да изтриеш този длъжник?");
    if (!ok) return;

    try {
      setDeleting(true);
      setErr("");
      setSelectedId("");
      setSelected(null);

      await deleteClientForm(id);

      setToast("Изтрито");

      if (filtered.length <= 1 && page > 1) {
        setPage((p) => Math.max(1, p - 1));
      } else {
        setReloadKey((k) => k + 1);
      }
    } catch (e) {
      setErr(e?.message || "Грешка при изтриване");
      setToast(e?.message || "Грешка при изтриване");
      setReloadKey((k) => k + 1);
    } finally {
      setDeleting(false);
    }
  };

  const doEdit = () => {
    const id = selectedMeta?.id || selectedId;
    if (!id) return;
    if (typeof onEdit === "function") onEdit(id);
  };

  const doOutboundCall = async () => {
    if (outboundBusy) return;
    if (!outboundPhone) {
      setToast(
        isThirdPersonScenario
          ? "Липсва телефон на трето лице за обаждане."
          : "Няма телефон за обаждане."
      );
      return;
    }
    if (!outboundClientId) {
      setToast("Липсва client id.");
      return;
    }

    setOutboundBusy(true);
    try {
      const latestClientCall = await ensureClientCallState(outboundClientId, { force: true });
      if (latestClientCall?.active || isClientCallActive(outboundClientId)) {
        setToast("За този длъжник вече има активно обаждане.");
        return;
      }

      const promptRes = await buildScenarioPrompt({ client_id: outboundClientId });
      const systemPrompt = String(promptRes?.prompt || "").trim();
      const firstMessage = String(promptRes?.first_message ?? promptRes?.firstMessage ?? "").trim();
      if (!systemPrompt) {
        throw new Error("Няма наличен промпт.");
      }

      await startCall({
        phone: outboundPhone,
        client_id: outboundClientId,
        system_prompt: systemPrompt,
        first_message: firstMessage,
      });
      markClientCallPending(outboundClientId);
      void refreshNotifications({ silent: true });
      void ensureClientCallState(outboundClientId, { force: true });
      setToast("Заявката за обаждане е изпратена.");
    } catch (e) {
      setToast(e?.message || "Грешка при обаждане.");
    } finally {
      setOutboundBusy(false);
    }
  };

  const doAdd = () => {
    if (typeof onAdd === "function") onAdd();
  };

  const scenarioNameById = useMemo(() => {
    const m = {};
    for (const s of scenarios || []) {
      if (s?.id) m[s.id] = s?.name || s.id;
    }
    return m;
  }, [scenarios]);

  function mapScenario(v) {
    if (!v) return "—";
    if (scenarioNameById[v]) return scenarioNameById[v];
    return dash(v);
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-bold">Длъжници</h1>

        <div className="flex-1" />

        <button className={BTN_PRIMARY} onClick={doAdd}>
          Добави длъжник
        </button>

        {typeof onBack === "function" && (
          <button className={BTN_GHOST} onClick={onBack}>
            Назад
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <section className={[CARD, "xl:col-span-1"].join(" ")}>
          <div className={H2}>Списък</div>

          <div className="mt-4">
            <input
              className={INP}
              placeholder="Търси (име, ЕГН, телефон, кредитор...)"
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPage(1);
              }}
            />
          </div>

          <div className="mt-4 flex items-center justify-between gap-3">
            <button
              className={BTN_PAG}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={loadingList || page <= 1}
              title="Предишна страница"
            >
              ←
            </button>

            <div className="text-xs text-slate-400 text-center">
              {total > 0 ? (
                <>
                  {fromIdx}–{toIdx} от {total} • Стр. {page}/{totalPages}
                </>
              ) : (
                <>Стр. {page}/{totalPages}</>
              )}
            </div>

            <button
              className={BTN_PAG}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={loadingList || page >= totalPages}
              title="Следваща страница"
            >
              →
            </button>
          </div>

          <div className="mt-4 space-y-2">
            {loadingList ? (
              <div className="text-sm text-slate-300">Зареждане...</div>
            ) : err ? (
              <div className="text-sm text-rose-300">{err}</div>
            ) : filtered.length === 0 ? (
              <div className="text-sm text-slate-300">Няма резултати.</div>
            ) : (
              filtered.map((x) => {
                const p = x || {};
                const debtsArr = getDebtsArray(p);
                const callDebtEntries = getCallDebtEntries(p, debtsArr);
                const primaryCallDebt = callDebtEntries[0] || (debtsArr[0] ? { debt: debtsArr[0], index: 0 } : null);
                const pd = primaryCallDebt?.debt || null;
                const pdPaid = primaryCallDebt ? isDebtPaid(p, pd, primaryCallDebt.index) : false;
                const extra = Math.max(0, callDebtEntries.length - 1);
                const isActive = p?.id === (selectedMeta?.id || selectedId);
                const latestHistory = getLatestClientHistoryEntry(p.history);

                return (
                  <button
                    key={p?.id}
                    onClick={() => setSelectedId(p?.id)}
                    className={[
                      "w-full text-left rounded-2xl border p-4 transition",
                      isActive
                        ? "border-violet-600/50 bg-violet-600/10"
                        : "border-[#1f2638] bg-[#0f1523] hover:bg-[#121825]",
                    ].join(" ")}
                  >
                    <div className="flex items-start gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-3">
                          <div className="font-semibold text-slate-200 truncate">
                            {dash(p.personal?.firstName)} {dash(p.personal?.lastName)}
                          </div>
                          <div className="text-xs text-slate-400 whitespace-nowrap">
                            {fmtDateTime(x?.createdAt)}
                          </div>
                        </div>

                        <div className="mt-1 text-xs text-slate-400 truncate">
                          ID: <span className="text-slate-200">{dash(x?.id)}</span> • ЕГН:{" "}
                          <span className="text-slate-200">{dash(p.personal?.egn)}</span>
                        </div>

                        <div className="mt-2 text-sm text-slate-200 flex flex-wrap gap-x-3 gap-y-1">
                          <span className="text-slate-400">Кредитор:</span> {dash(pd?.creditor)}
                          <span className="text-slate-400">Сума:</span> {dashMoney(pd?.amount)} €
                          {pdPaid && (
                            <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded border border-emerald-500/30 bg-emerald-600/15 text-emerald-200 whitespace-nowrap">
                              платено
                            </span>
                          )}
                          {extra > 0 && (
                            <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded border border-[#1f2638] bg-[#0e1320]/60 text-slate-300">
                              +{extra} още
                            </span>
                          )}
                        </div>

                        <div className="mt-1 text-xs text-slate-400">
                          Резултат:{" "}
                          <span className="text-slate-200">
                            {mapCallResult(latestHistory.lastCallResult)}
                          </span>
                        </div>

                        <div className="mt-1 text-xs text-slate-400">
                          Сценарий: <span className="text-slate-200">{mapScenario(p?.scenario)}</span>
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </section>

        <section className="xl:col-span-2 space-y-6">
          {!selectedId ? (
            <div className={CARD}>Няма избран длъжник.</div>
          ) : loadingDetail && !payload ? (
            <div className={CARD}>Зареждане на детайл...</div>
          ) : !payload ? (
            <div className={CARD}>Няма данни за избрания длъжник.</div>
          ) : (
            <>
              <div className={CARD}>
                <div className="flex items-center gap-3">
                  <div className={H2}>ОБЩО</div>
                  <div className="flex-1" />
                  <button
                    className={BTN}
                    onClick={doEdit}
                    disabled={deleting || loadingDetail}
                    title="Редакция"
                  >
                    Промени
                  </button>
                  <button
                    className={BTN_DANGER}
                    onClick={doDelete}
                    disabled={deleting || loadingDetail}
                    title="Изтрий"
                  >
                    {deleting ? "Триене..." : "Изтрий"}
                  </button>
                  <button
                    className={BTN2}
                    onClick={doOutboundCall}
                    disabled={outboundDisabled}
                    title={outboundButtonTitle}
                  >
                    {outboundBusy ? "Обаждане..." : "Обаждане по номер"}
                  </button>
                  <ClientWebCallV2Button client={payload} />
                </div>

                {outboundCallActive && !outboundBusy && (
                  <div className="mt-3 text-xs text-amber-200">
                    За този длъжник в момента се провежда разговор.
                  </div>
                )}

                <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                  <div>
                    <div className={LBL}>ID</div>
                    <div className="text-slate-200">{dash(selectedMeta?.id)}</div>
                  </div>
                  <div>
                    <div className={LBL}>Сценарий</div>
                    <div className="text-slate-200">{mapScenario(payload?.scenario)}</div>
                  </div>
                  <div>
                    <div className={LBL}>Създадена</div>
                    <div className="text-slate-200">{fmtDateTime(selectedMeta?.createdAt)}</div>
                  </div>
                  <div>
                    <div className={LBL}>Дата на последен разговор</div>
                    <div className="text-slate-200">{fmtDateISO(latestPayloadHistory.lastCallDate)}</div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <section className={CARD}>
                  <div className={H2}>ЛИЧНИ ДАННИ</div>
                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                    <div>
                      <div className={LBL}>Име</div>
                      <div className="text-slate-200">{dash(payload.personal?.firstName)}</div>
                    </div>
                    <div>
                      <div className={LBL}>Фамилия</div>
                      <div className="text-slate-200">{dash(payload.personal?.lastName)}</div>
                    </div>
                    <div className="sm:col-span-2">
                      <div className={LBL}>ЕГН</div>
                      <div className="text-slate-200">{dash(payload.personal?.egn)}</div>
                    </div>
                  </div>
                </section>

                <section className={CARD}>
                  <div className={H2}>КОНТАКТИ</div>
                  <div className="mt-4 grid grid-cols-1 gap-4 text-sm">
                    <div>
                      <div className={LBL}>Телефон</div>
                      <div className="text-slate-200">{dash(payload.contacts?.phone)}</div>
                    </div>
                    <div>
                      <div className={LBL}>Трето лице</div>
                      <div className="text-slate-200">{mapYesNo(payload.contacts?.thirdPerson)}</div>
                    </div>

                    {payload.contacts?.thirdPerson === "yes" ? (
                      <>
                        <div>
                          <div className={LBL}>Телефон (трето лице)</div>
                          <div className="text-slate-200">{dash(payload.contacts?.thirdPersonPhone)}</div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <div className={LBL}>Име (трето лице)</div>
                            <div className="text-slate-200">{dash(payload.contacts?.thirdPersonFirstName)}</div>
                          </div>

                          <div>
                            <div className={LBL}>Фамилия (трето лице)</div>
                            <div className="text-slate-200">{dash(payload.contacts?.thirdPersonLastName)}</div>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="text-xs text-slate-500">
                        Няма данни за трето лице.
                      </div>
                    )}
                  </div>
                </section>

                <section className={CARD}>
                  <div className={H2}>ЗАДЪЛЖЕНИЯ</div>

                  {(() => {
                    const debtsArr = getDebtsArray(payload);
                    const callDebtIndexSet = new Set(
                      getCallDebtEntries(payload, debtsArr).map((entry) => entry.index)
                    );
                    const isRegularPayerScenario = isRegularPayerScenarioSelected(payload?.scenario, scenarios);
                    const isBrokenPromiseScenario = isBrokenPromiseScenarioSelected(payload?.scenario, scenarios);
                    const hasInstallmentData = debtsArr.some((d) => {
                      const installment = String(d?.installment ?? d?.installment_amount ?? "").trim();
                      return !!installment;
                    });
                    const hasDueDateData = debtsArr.some((d) => {
                      const dueDate = String(d?.dueDate ?? d?.due_date ?? "").trim();
                      return !!dueDate;
                    });
                    const showInstallmentFields = isRegularPayerScenario || isBrokenPromiseScenario || hasInstallmentData;
                    const showDueDateFields = isBrokenPromiseScenario || hasDueDateData;

                    if (!debtsArr.length) {
                      return <div className="mt-4 text-sm text-slate-400">Няма задължения.</div>;
                    }

                    return (
                      <div className="mt-4 space-y-2 text-sm">
                        {debtsArr.map((d, i) => {
                          const paid = isDebtPaid(payload, d, i);

                          return (
                            <div
                              key={i}
                              className="flex items-center justify-between gap-3 rounded-xl border border-[#1f2638] bg-[#0f1523] p-3"
                            >
                              <div className="min-w-0">
                                <div className="text-slate-200 font-semibold truncate">{dash(d?.creditor)}</div>
                                <div className="text-slate-400">{dashMoney(d?.amount)} €</div>
                                {(showInstallmentFields || showDueDateFields) && (
                                  <div className="mt-1 space-y-0.5 text-slate-400">
                                    {showInstallmentFields && (
                                      <div>Вноска: {dashMoney(d?.installment ?? d?.installment_amount)} €</div>
                                    )}
                                    {showDueDateFields && (
                                      <div>Падеж: {fmtDateISO(d?.dueDate ?? d?.due_date)}</div>
                                    )}
                                  </div>
                                )}
                              </div>

                              <div className="flex items-center gap-2">
                                <span
                                  className={[
                                    "text-[10px] px-1.5 py-0.5 rounded border whitespace-nowrap",
                                    paid
                                      ? "border-emerald-500/30 bg-emerald-600/15 text-emerald-200"
                                      : "border-[#1f2638] bg-[#0e1320]/60 text-slate-300",
                                  ].join(" ")}
                                >
                                  {paid ? "платено" : "неплатено"}
                                </span>

                                {callDebtIndexSet.has(i) && (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded border border-emerald-500/30 bg-emerald-600/15 text-emerald-200 whitespace-nowrap">
                                    за обаждане
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </section>

                <section className={CARD}>
                  <div className={H2}>ПЛАТЕНИ ЗАДЪЛЖЕНИЯ</div>

                  {(() => {
                    const debtsArr = getDebtsArray(payload);

                    const paidOnly = debtsArr
                      .map((d, i) => ({ d, i, paid: isDebtPaid(payload, d, i) }))
                      .filter((x) => x.paid);

                    if (!paidOnly.length) {
                      return <div className="mt-4 text-sm text-slate-400">Няма платени задължения.</div>;
                    }

                    return (
                      <div className="mt-4 space-y-2 text-sm">
                        {paidOnly.map(({ d, i }) => (
                          <div
                            key={`paid_${i}`}
                            className="flex items-center justify-between gap-3 rounded-xl border border-[#1f2638] bg-[#0f1523] p-3"
                          >
                            <div className="min-w-0">
                              <div className="text-slate-200 font-semibold truncate">{dash(d?.creditor)}</div>
                              <div className="text-slate-400">{dashMoney(d?.amount)} €</div>
                            </div>

                            <span className="text-[10px] px-1.5 py-0.5 rounded border border-emerald-500/30 bg-emerald-600/15 text-emerald-200 whitespace-nowrap">
                              платено
                            </span>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </section>

                <section className={CARD}>
                  <div className={H2}>ИСТОРИЯ</div>
                  {payloadHistoryEntries.length ? (
                    <div className="mt-4 space-y-2 text-sm">
                      {payloadHistoryEntries.map((entry, i) => (
                        <div
                          key={`history_${i}`}
                          className="grid grid-cols-1 sm:grid-cols-2 gap-3 rounded-xl border border-[#1f2638] bg-[#0f1523] p-3"
                        >
                          <div>
                            <div className={LBL}>Дата на разговор</div>
                            <div className="text-slate-200">{fmtDateISO(entry.lastCallDate)}</div>
                          </div>
                          <div>
                            <div className={LBL}>Резултат</div>
                            <div className="text-slate-200">{mapCallResult(entry.lastCallResult)}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="mt-4 text-sm text-slate-400">Няма записи.</div>
                  )}
                </section>

                <section className={CARD}>
                  <div className={H2}>РАБОТА</div>
                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                    <div>
                      <div className={LBL}>Работи ли</div>
                      <div className="text-slate-200">{mapYesNo(payload.work?.isWorking)}</div>
                    </div>
                    <div>
                      <div className={LBL}>Заплата (евро)</div>
                      <div className="text-slate-200">{dash(payload.work?.salary)}</div>
                    </div>
                  </div>
                </section>

                <section className={CARD}>
                  <div className={H2}>БЕЛЕЖКИ</div>
                  <div className="mt-4 text-sm text-slate-200 whitespace-pre-wrap">
                    {dash(payload.notes)}
                  </div>
                </section>
              </div>
            </>
          )}
        </section>
      </div>

      {toast && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[60]">
          <div className="rounded-xl border border-slate-800/60 bg-[#0f1523] px-5 py-3 shadow-lg text-sm text-slate-200">
            {toast}
          </div>
        </div>
      )}
    </div>
  );
}
